/**
 * Stop Detail Page
 * Gestion détaillée d'un arrêt client avec chrono et produits
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { 
  ArrowLeft, Play, Square, Clock, DollarSign, 
  TrendingUp, Package, Plus, Trash2, Edit2, Save, X 
} from 'lucide-react';
import '../../../styles/admin/production/StopDetail.css';

const StopDetail = () => {
  const { stopId } = useParams();
  const navigate = useNavigate();
  
  const [stop, setStop] = useState(null);
  const [products, setProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Modal ajout produit
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    product_id: '',
    quantity_used: 0,
    unit: '',
    unit_cost: 0
  });

  useEffect(() => {
    fetchStopData();
  }, [stopId]);

  const fetchStopData = async () => {
    try {
      setLoading(true);
      const [stopRes, productsRes, catalogRes] = await Promise.all([
        api.get(`/api/production/stops/${stopId}`),
        api.get(`/api/production/stops/${stopId}/products`),
        api.get(`/api/production/products?is_active=true`)
      ]);
      
      setStop(stopRes.data);
      setProducts(productsRes.data);
      setAvailableProducts(catalogRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement stop:', error);
      setLoading(false);
    }
  };

  const handleStartStop = async () => {
    if (!window.confirm('Démarrer le service ? Le chronomètre va commencer.')) return;
    
    try {
      setActionLoading(true);
      await api.post(`/api/production/stops/${stopId}/start`);
      await fetchStopData();
      setActionLoading(false);
    } catch (error) {
      console.error('Erreur démarrage stop:', error);
      alert(error.response?.data?.detail || 'Erreur lors du démarrage');
      setActionLoading(false);
    }
  };

  const handleEndStop = async () => {
    if (!window.confirm('Terminer le service ? Les calculs de rentabilité seront finalisés.')) return;
    
    try {
      setActionLoading(true);
      await api.post(`/api/production/stops/${stopId}/end`);
      await fetchStopData();
      setActionLoading(false);
    } catch (error) {
      console.error('Erreur fin stop:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la fin du service');
      setActionLoading(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    try {
      setActionLoading(true);
      await api.post(`/api/production/stops/${stopId}/products`, newProduct);
      await fetchStopData();
      setShowAddProduct(false);
      setNewProduct({ product_id: '', quantity_used: 0, unit: '', unit_cost: 0 });
      setActionLoading(false);
    } catch (error) {
      console.error('Erreur ajout produit:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'ajout du produit');
      setActionLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    
    try {
      await api.delete(`/api/production/stops/${stopId}/products/${productId}`);
      await fetchStopData();
    } catch (error) {
      console.error('Erreur suppression produit:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleProductSelect = (productId) => {
    const selected = availableProducts.find(p => p.id === productId);
    if (selected) {
      setNewProduct({
        product_id: selected.id,
        quantity_used: 0,
        unit: selected.unit,
        unit_cost: selected.cost_per_unit
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'En attente', className: 'stop-pending' },
      in_progress: { label: 'En cours', className: 'stop-in-progress' },
      completed: { label: 'Terminé', className: 'stop-completed' },
      skipped: { label: 'Sauté', className: 'stop-skipped' },
      cancelled: { label: 'Annulé', className: 'stop-cancelled' }
    };
    const s = statusMap[status] || { label: status, className: '' };
    return <span className={`status-badge ${s.className}`}>{s.label}</span>;
  };

  if (loading) {
    return <div className="loading-container">Chargement...</div>;
  }

  if (!stop) {
    return <div className="error-container">Stop introuvable</div>;
  }

  const canStartStop = stop.status === 'pending';
  const canEndStop = stop.status === 'in_progress';
  const canModify = stop.status !== 'completed';
  const revenue = stop.actual_revenue || stop.planned_revenue;

  return (
    <div className="stop-detail-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Retour
          </button>
          <div className="stop-title">
            <h1>Stop #{stop.stop_number} - {stop.client_name}</h1>
            <p className="stop-subtitle">{stop.address}</p>
          </div>
        </div>
        <div className="header-actions">
          {canStartStop && (
            <button 
              className="btn-success" 
              onClick={handleStartStop}
              disabled={actionLoading}
            >
              <Play size={18} />
              Démarrer Service
            </button>
          )}
          {canEndStop && (
            <button 
              className="btn-danger" 
              onClick={handleEndStop}
              disabled={actionLoading}
            >
              <Square size={18} />
              Terminer Service
            </button>
          )}
        </div>
      </div>

      <div className="stop-content">
        {/* Colonne Gauche - Infos & Timing */}
        <div className="left-column">
          {/* Infos Générales */}
          <div className="section-card">
            <h3>Informations Générales</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Statut</span>
                <div>{getStatusBadge(stop.status)}</div>
              </div>
              <div className="info-item">
                <span className="info-label">Service</span>
                <span className="service-badge">{stop.service_type}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Code Service</span>
                <span>{stop.service_code || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Facturable</span>
                <span className={stop.is_billable ? 'text-success' : 'text-muted'}>
                  {stop.is_billable ? 'Oui' : 'Non'}
                </span>
              </div>
            </div>
          </div>

          {/* Chronomètre */}
          <div className="section-card timing-card">
            <div className="timing-header">
              <Clock size={24} />
              <h3>Timing</h3>
            </div>
            
            {stop.status === 'pending' && (
              <div className="timing-status pending">
                <p>Service non démarré</p>
                <span>Cliquez sur "Démarrer Service" pour commencer le chrono</span>
              </div>
            )}
            
            {stop.status === 'in_progress' && (
              <div className="timing-status in-progress">
                <div className="timer-display">
                  <Clock size={48} className="timer-icon" />
                  <div className="timer-text">
                    <div className="timer-label">En cours...</div>
                    <div className="timer-start">
                      Début: {stop.started_at ? new Date(stop.started_at).toLocaleTimeString('fr-FR') : '-'}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {stop.status === 'completed' && (
              <div className="timing-status completed">
                <div className="timing-details">
                  <div className="timing-row">
                    <span>Début:</span>
                    <strong>{stop.started_at ? new Date(stop.started_at).toLocaleTimeString('fr-FR') : '-'}</strong>
                  </div>
                  <div className="timing-row">
                    <span>Fin:</span>
                    <strong>{stop.ended_at ? new Date(stop.ended_at).toLocaleTimeString('fr-FR') : '-'}</strong>
                  </div>
                  <div className="timing-row total">
                    <span>Durée Totale:</span>
                    <strong className="duration-highlight">{stop.total_minutes} minutes</strong>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Photos Placeholder */}
          <div className="section-card">
            <h3>Photos</h3>
            <div className="photos-placeholder">
              <Package size={48} />
              <p>Fonctionnalité à venir</p>
              <span className="placeholder-text">L'ajout de photos sera disponible prochainement</span>
            </div>
          </div>
        </div>

        {/* Colonne Droite - Financier & Produits */}
        <div className="right-column">
          {/* KPIs Financiers */}
          <div className="section-card">
            <h3>Rentabilité du Stop</h3>
            <div className="financial-kpis">
              <div className="kpi-item revenue">
                <div className="kpi-icon"><DollarSign size={20} /></div>
                <div className="kpi-details">
                  <span className="kpi-label">Revenus</span>
                  <span className="kpi-value">${revenue.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="kpi-item cost">
                <div className="kpi-icon"><TrendingUp size={20} /></div>
                <div className="kpi-details">
                  <span className="kpi-label">Coûts Totaux</span>
                  <span className="kpi-value">${stop.total_cost.toFixed(2)}</span>
                  <span className="kpi-breakdown">
                    MO: ${stop.labor_cost.toFixed(2)} • Produits: ${stop.product_cost.toFixed(2)}
                  </span>
                </div>
              </div>
              
              <div className="kpi-item profit">
                <div className="kpi-icon"><TrendingUp size={20} /></div>
                <div className="kpi-details">
                  <span className="kpi-label">Profit</span>
                  <span className={`kpi-value ${stop.gross_profit >= 0 ? 'positive' : 'negative'}`}>
                    ${stop.gross_profit.toFixed(2)}
                  </span>
                </div>
              </div>
              
              <div className="kpi-item margin">
                <div className="kpi-icon"><TrendingUp size={20} /></div>
                <div className="kpi-details">
                  <span className="kpi-label">Marge</span>
                  <span className={`kpi-value ${stop.margin_percent >= 0 ? 'positive' : 'negative'}`}>
                    {stop.margin_percent.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Produits Utilisés */}
          <div className="section-card products-card">
            <div className="products-header">
              <h3>Produits Utilisés</h3>
              {canModify && (
                <button 
                  className="btn-primary-small" 
                  onClick={() => setShowAddProduct(true)}
                >
                  <Plus size={16} />
                  Ajouter
                </button>
              )}
            </div>

            {products.length === 0 ? (
              <div className="no-products">
                <Package size={48} />
                <p>Aucun produit utilisé</p>
              </div>
            ) : (
              <div className="products-list">
                {products.map((product) => (
                  <div key={product.id} className="product-item">
                    <div className="product-info">
                      <div className="product-name">{product.product_name}</div>
                      <div className="product-details">
                        {product.quantity_used} {product.unit} × ${product.unit_cost.toFixed(2)}
                      </div>
                    </div>
                    <div className="product-actions">
                      <span className="product-cost">${product.total_cost.toFixed(2)}</span>
                      {canModify && (
                        <button 
                          className="btn-icon-delete"
                          onClick={() => handleDeleteProduct(product.id)}
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          {stop.notes && (
            <div className="section-card">
              <h3>Notes</h3>
              <p className="notes-text">{stop.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal Ajout Produit */}
      {showAddProduct && (
        <div className="modal-overlay" onClick={() => setShowAddProduct(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajouter un Produit</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddProduct(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddProduct} className="modal-form">
              <div className="form-group">
                <label>Produit *</label>
                <select
                  required
                  value={newProduct.product_id}
                  onChange={(e) => handleProductSelect(e.target.value)}
                >
                  <option value="">Sélectionner un produit</option>
                  {availableProducts.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.sku || 'N/A'})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Quantité *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={newProduct.quantity_used}
                    onChange={(e) => setNewProduct({...newProduct, quantity_used: parseFloat(e.target.value)})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Unité *</label>
                  <input
                    type="text"
                    required
                    value={newProduct.unit}
                    onChange={(e) => setNewProduct({...newProduct, unit: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Coût Unitaire ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={newProduct.unit_cost}
                  onChange={(e) => setNewProduct({...newProduct, unit_cost: parseFloat(e.target.value)})}
                />
              </div>
              
              <div className="cost-preview">
                <span>Coût Total:</span>
                <strong>${(newProduct.quantity_used * newProduct.unit_cost).toFixed(2)}</strong>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowAddProduct(false)}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={actionLoading}
                >
                  <Save size={16} />
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StopDetail;
